import { Alert, Button, Field, Modal } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, type JSX } from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import { ZONE_LABELS, ZONES } from '../../../../calendar/api';
import {
  DEFAULT_HOLIDAY_FORM_VALUES,
  HOLIDAY_FORM_GLOBAL_ZONE,
  holidayFormSchema,
  type HolidayFormValues,
} from '../../../../calendar/holiday-form-schema';

export interface HolidayFormModalProps {
  mode: 'create' | 'edit';
  defaultValues?: HolidayFormValues | undefined;
  submitting: boolean;
  formError?: string | undefined;
  onCancel: () => void;
  onSubmit: (values: HolidayFormValues) => Promise<void>;
}

/**
 * `holidays/spec.md` → "Alta de feriado" / "Edición de feriado". `zone`/
 * `date` render read-only in edit mode: both form the holiday's uniqueness
 * key and the API rejects changing them (`HOLIDAY_IMMUTABLE_FIELDS`), same
 * criterion `MenuCalendarEntryModal` already applies to `serviceDate`/
 * `menuId`.
 */
export function HolidayFormModal({
  mode,
  defaultValues,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: HolidayFormModalProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<HolidayFormValues, unknown, HolidayFormValues>({
    resolver: zodResolver(holidayFormSchema) as unknown as Resolver<
      HolidayFormValues,
      unknown,
      HolidayFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_HOLIDAY_FORM_VALUES,
  });

  function handleFormSubmit(event: FormEvent): void {
    void handleSubmit((values) => onSubmit(values))(event);
  }

  const isEdit = mode === 'edit';

  return (
    <Modal
      open
      onClose={onCancel}
      title={isEdit ? 'Editar feriado' : 'Nuevo feriado'}
      className="max-w-lg"
    >
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="holiday-form-zone" className="text-sm font-medium text-slate-900">
            Zona
          </label>
          <select
            id="holiday-form-zone"
            disabled={isEdit}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-500"
            {...register('zone')}
          >
            <option value={HOLIDAY_FORM_GLOBAL_ZONE}>Todas las zonas</option>
            {ZONES.map((zone) => (
              <option key={zone} value={zone}>
                {ZONE_LABELS[zone]}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500">
            Un feriado de zona específica tiene prioridad sobre uno de todas las zonas en la misma
            fecha.
          </p>
        </div>

        <Field
          label="Fecha"
          type="date"
          required
          disabled={isEdit}
          {...register('date')}
          {...maybeError(errors.date?.message)}
        />

        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300"
            {...register('hasService')}
          />
          Hay servicio
        </label>

        <Field
          label="Descripción"
          {...register('description')}
          {...maybeError(errors.description?.message)}
        />

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            {isEdit ? 'Guardar cambios' : 'Crear feriado'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}
